import { useQuery } from '@tanstack/react-query';
import { queryKeys, configurationsService } from '@v2/services';
import type { PimsConfiguration } from '@v2/types';

interface UsePimsConfigurationsResult {
  configurations: PimsConfiguration[];
  loading: boolean;
  error: string | null;
}

/**
 * Fetches PIMS configurations from the universal configs endpoint.
 *
 * The standard GET /api/Pims/{id} only reads from the legacy
 * ConfigurationPimses table, but new configs are written to
 * ConfigurationUniversals. This hook uses ReadUniversalConfigs
 * which merges both tables.
 */
export const usePimsConfigurations = (pimsId: string | null): UsePimsConfigurationsResult => {
  const query = useQuery({
    queryKey: queryKeys.configurations.byPims(pimsId ?? ''),
    queryFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const items = await configurationsService.getByPimsId(pimsId!);
      return items
        .map(
          (item): PimsConfiguration => ({
            id: item.selectedSourceId ?? '',
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            entityId: pimsId!,
            entityType: item.selectedSourceEntity ?? 'ConfigurationPims',
            key: item.key,
            value: item.value ?? '',
          }),
        )
        .sort((a, b) => a.key.localeCompare(b.key));
    },
    enabled: pimsId != null && pimsId.length > 0,
  });

  return {
    configurations: query.data ?? [],
    loading: query.isLoading,
    error: query.error instanceof Error ? query.error.message : null,
  };
};

export default usePimsConfigurations;
